import { useEffect, useRef } from 'react';
import Theme from '../styles/Theme.js';
import Utils from '../Utils.js';

/*
 * StepFrame
 *
 * Every step in every tree is drawn inside this: an optional eyebrow, a
 * question, an optional line of help, the step's own content, and the actions.
 * Six node types share one silhouette, so moving through the tree feels like
 * one conversation rather than six different screens.
 *
 * IT ALSO OWNS THE ANNOUNCEMENT. The page never navigates. React swaps the
 * content and the URL does not change, so a screen reader has no reason to say
 * anything at all. Focus is moved to the heading on every step, which is what
 * makes the next question get read out. Without this the flow is silently
 * unusable with a screen reader, and that is not a defect anybody would catch
 * by looking at it.
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const StepFrame = ({
  actions,
  children,
  eyebrow,
  footnote,
  help,
  stepKey,
  title
}) => {
  const heading = useRef(null);
  const mobile = Utils.isMobile();

  // Re-run per step, not per render, `stepKey` is the node id.
  useEffect(() => {
    heading.current?.focus({
      preventScroll: true
    });
    window.scrollTo({
      behavior: 'smooth',
      top: 0
    });
  }, [stepKey]);
  return /*#__PURE__*/_jsxs("div", {
    children: [eyebrow && /*#__PURE__*/_jsx("span", {
      style: {
        ...Theme.textStyles.label(),
        display: 'block',
        marginBottom: 12
      },
      children: eyebrow
    }), /*#__PURE__*/_jsx("h1", {
      className: 'support-step-heading',
      ref: heading,
      tabIndex: -1,
      style: {
        ...Theme.textStyles.heading({
          fontSize: mobile ? 25 : 32
        }),
        marginBottom: help ? 10 : mobile ? 24 : 30,
        /*
         * Inline, not in the stylesheet, and that is deliberate.
         *
         * The package's rule and a host's own `[tabindex]:focus-visible`
         * have identical specificity, so whichever stylesheet is
         * imported second wins. The host's usually is, which is why the
         * ring came back: the rule was there and simply lost.
         *
         * An inline style beats both without needing !important and
         * without caring what order anybody imports anything in, which
         * is the only version of this that survives being dropped into
         * an application we have not seen.
         */
        outline: 'none'
      },
      children: title
    }), help && /*#__PURE__*/_jsx("p", {
      style: {
        ...Theme.textStyles.body({
          fontSize: mobile ? 15 : 16
        }),
        marginBottom: mobile ? 24 : 30
      },
      children: help
    }), children, actions && /*#__PURE__*/_jsx("div", {
      style: {
        alignItems: mobile ? 'stretch' : 'center',
        display: 'flex',
        flexDirection: mobile ? 'column' : 'row',
        flexWrap: mobile ? 'nowrap' : 'wrap',
        gap: 12,
        marginTop: mobile ? 28 : 36
      },
      children: actions
    }), footnote && /*#__PURE__*/_jsx("p", {
      style: {
        ...Theme.textStyles.caption(),
        marginTop: 20
      },
      children: footnote
    })]
  });
};
export default StepFrame;